/**
 * External / guest file-access aggregation (C13). Pure and deterministic.
 *
 * Turns the flat list of external file-permission grants into a guest-level view
 * (which files each external user can reach) and a site-level overview (how many
 * external users and files are exposed per site).
 */
import type { ExternalGrant } from '@/graph/modules/guestAccess';

export interface GuestFileRef {
  site: string;
  file: string;
  webUrl: string;
  role: string;
  kind: ExternalGrant['kind'];
}

export interface GuestFileAccess {
  id: string | null;
  name: string;
  email: string | null;
  fileCount: number;
  siteCount: number;
  roles: string[];
  files: GuestFileRef[];
}

export interface SiteExposure {
  site: string;
  guests: number;
  files: number;
}

export interface GuestAccessResult {
  totalGuests: number; // roster size
  guestsWithFileAccess: number;
  filesExposed: number;
  sitesScanned: number;
  bySite: SiteExposure[];
  byGuest: GuestFileAccess[];
}

export function aggregateGuestAccess(
  rosterCount: number,
  sitesScanned: number,
  grants: readonly ExternalGrant[],
): GuestAccessResult {
  const byGuest = new Map<string, GuestFileAccess>();
  const bySite = new Map<string, { site: string; guests: Set<string>; files: Set<string> }>();

  for (const g of grants) {
    const key = g.granteeId ?? g.granteeEmail ?? g.granteeName;

    let entry = byGuest.get(key);
    if (!entry) {
      entry = { id: g.granteeId, name: g.granteeName, email: g.granteeEmail, fileCount: 0, siteCount: 0, roles: [], files: [] };
      byGuest.set(key, entry);
    }
    entry.files.push({ site: g.siteName, file: g.fileName, webUrl: g.webUrl, role: g.role, kind: g.kind });
    if (!entry.roles.includes(g.role)) entry.roles.push(g.role);

    let site = bySite.get(g.siteId);
    if (!site) {
      site = { site: g.siteName, guests: new Set(), files: new Set() };
      bySite.set(g.siteId, site);
    }
    site.guests.add(key);
    site.files.add(g.fileId);
  }

  const byGuestList = [...byGuest.values()]
    .map((e) => ({
      ...e,
      fileCount: e.files.length,
      siteCount: new Set(e.files.map((f) => f.site)).size,
    }))
    .sort((a, b) => b.fileCount - a.fileCount);

  const bySiteList = [...bySite.values()]
    .map((s) => ({ site: s.site, guests: s.guests.size, files: s.files.size }))
    .sort((a, b) => b.files - a.files);

  return {
    totalGuests: rosterCount,
    guestsWithFileAccess: byGuest.size,
    filesExposed: new Set(grants.map((g) => g.fileId)).size,
    sitesScanned,
    bySite: bySiteList,
    byGuest: byGuestList,
  };
}
